import { Component } from '@theme/component';

const CLOSE_DELAY = 120;
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * The AW Hekken masthead: mega menu, mobile drawer, and the header height
 * contract the rest of Horizon depends on.
 *
 * Ported from aw-design/react/MegaMenu.js and Drawer.js — same UX rules, same
 * markup, so aw-design/css/header.css transfers rather than being rewritten.
 *
 * Search and the cart drawer are NOT here. Both are rendered globally by
 * layout/theme.liquid (lines 186 and 189) and opened declaratively from the
 * markup, so this file never touches them.
 *
 * The tag ends in `-component` on purpose: assets/component.js only runs its
 * upgrade-race guard for such tags, so without it a click landing before this
 * module executes is dropped silently.
 *
 * @extends {Component}
 */
class AwhHeader extends Component {
  #closeTimer = null;
  #openPanel = null;
  #drawerOpen = false;
  #hasOpenedDrawer = false;
  #resizeObserver = null;
  #scrollLock = null;

  connectedCallback() {
    super.connectedCallback();

    this.#publishHeights();
    this.#observeHeights();

    this.addEventListener('pointerenter', this.#onPointerEnter, true);
    this.addEventListener('pointerleave', this.#onPointerLeave, true);
    this.addEventListener('focusin', this.#onFocusIn);
    this.addEventListener('focusout', this.#onFocusOut);
    document.addEventListener('keydown', this.#onKeydown);
    document.addEventListener('pointerdown', this.#onDocumentPointerDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cancelClose();
    this.#resizeObserver?.disconnect();
    this.#releaseScrollLock();
    this.removeEventListener('pointerenter', this.#onPointerEnter, true);
    this.removeEventListener('pointerleave', this.#onPointerLeave, true);
    this.removeEventListener('focusin', this.#onFocusIn);
    this.removeEventListener('focusout', this.#onFocusOut);
    document.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener('pointerdown', this.#onDocumentPointerDown);
  }

  /* ----------------------------------------------------------------------
     The height contract.

     layout/theme.liquid:80 measures #header-component and #header-group once
     before first paint and writes --header-height and --header-group-height
     onto <body>. Six unrelated files read them, snippets/section.liquid:101
     among them — which means EVERY section on the site. Horizon's own
     header.js keeps them fresh with ResizeObservers after load; ours has to
     do the same or sticky product details, the cart summary and the hero all
     drift the moment the header changes height.
     -------------------------------------------------------------------- */

  #publishHeights = () => {
    const group = document.querySelector('#header-group');
    if (!group) return;

    const headerHeight = this.offsetHeight;

    let groupHeight = 0;
    for (const child of group.children) {
      if (child instanceof HTMLElement) groupHeight += child.offsetHeight;
    }

    document.body.style.setProperty('--header-height', `${headerHeight}px`);
    document.body.style.setProperty('--header-group-height', `${groupHeight}px`);
  };

  #observeHeights = () => {
    if (typeof ResizeObserver === 'undefined') return;

    const group = document.querySelector('#header-group');
    this.#resizeObserver = new ResizeObserver(() => {
      // rAF so a resize triggered mid-layout does not force a second reflow.
      window.requestAnimationFrame(this.#publishHeights);
    });

    this.#resizeObserver.observe(this);
    if (group) this.#resizeObserver.observe(group);
  };

  /* ----------------------------------------------------------------------
     Mega menu
     -------------------------------------------------------------------- */

  /**
   * Opens on hover AND on click, so pointer and keyboard get the same affordance.
   *
   * `event.target`, not `currentTarget`: component.js binds these listeners on
   * `document` and proxies `target` to the element carrying the `on:` attribute
   * (component.js:246). `currentTarget` is therefore `document`.
   */
  toggle = (event) => {
    const trigger = event.target;
    const key = trigger?.dataset?.trigger;
    if (!key) return;
    this.#setPanel(this.#openPanel === key ? null : key);
  };

  #setPanel = (key) => {
    if (this.#openPanel === key) return;
    this.#openPanel = key;

    for (const item of this.querySelectorAll('[data-nav-item]')) {
      const isOpen = item.dataset.navItem === key;
      const trigger = item.querySelector('[data-trigger]');
      const panel = item.querySelector('[data-panel]');
      if (!panel) continue;

      panel.classList.toggle('is-open', isOpen);
      panel.hidden = !isOpen;
      trigger?.setAttribute('aria-expanded', String(isOpen));
    }
  };

  #cancelClose = () => {
    if (this.#closeTimer) {
      window.clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }
  };

  /** Delayed so a diagonal mouse path into the panel does not snap it shut. */
  #scheduleClose = () => {
    this.#cancelClose();
    this.#closeTimer = window.setTimeout(() => this.#setPanel(null), CLOSE_DELAY);
  };

  #onPointerEnter = (event) => {
    const item = event.target?.closest?.('[data-nav-item]');
    if (!item || !this.contains(item)) return;
    if (!item.querySelector('[data-panel]')) return;
    this.#cancelClose();
    this.#setPanel(item.dataset.navItem);
  };

  #onPointerLeave = (event) => {
    const item = event.target?.closest?.('[data-nav-item]');
    if (!item || !this.contains(item)) return;
    this.#scheduleClose();
  };

  #onFocusIn = (event) => {
    const item = event.target?.closest?.('[data-nav-item]');
    if (!item) return;
    if (!item.querySelector('[data-panel]')) return;
    this.#cancelClose();
    this.#setPanel(item.dataset.navItem);
  };

  /** Tabbing forward out of an item must never leave an orphaned open panel. */
  #onFocusOut = (event) => {
    const item = event.target?.closest?.('[data-nav-item]');
    if (!item) return;
    if (item.contains(event.relatedTarget)) return;
    this.#scheduleClose();
  };

  #onDocumentPointerDown = (event) => {
    if (this.#openPanel && !this.contains(event.target)) this.#setPanel(null);
  };

  /* ----------------------------------------------------------------------
     Drawer
     -------------------------------------------------------------------- */

  openDrawer = () => this.#setDrawer(true);
  closeDrawer = () => this.#setDrawer(false);

  /** The disclosures inside the drawer. One open at a time is NOT enforced —
      the prototype lets several stand open, which is right on a small screen
      where the alternative is losing your place on every tap. */
  toggleDisclosure = (event) => {
    const trigger = event.target;                 // see toggle() above
    const panel = trigger?.nextElementSibling;
    if (!panel) return;
    const isOpen = trigger.getAttribute('aria-expanded') === 'true';
    trigger.setAttribute('aria-expanded', String(!isOpen));
    panel.hidden = isOpen;
    panel.classList.toggle('is-open', !isOpen);
  };

  #setDrawer = (open) => {
    const drawer = this.querySelector('[data-drawer]');
    const scrim = this.querySelector('[data-drawer-scrim]');
    const burger = this.querySelector('[data-burger]');
    if (!drawer) return;

    this.#drawerOpen = open;
    drawer.classList.toggle('is-open', open);
    drawer.inert = !open;
    scrim?.classList.toggle('is-open', open);
    if (scrim) scrim.hidden = !open;
    burger?.setAttribute('aria-expanded', String(open));

    if (open) {
      this.#hasOpenedDrawer = true;
      this.#applyScrollLock();
      // Land on the close button rather than the logo — it is the action a
      // returning user most often wants, and it announces the way out.
      const closeBtn = drawer.querySelector('[data-drawer-close]');
      (closeBtn ?? drawer.querySelector(FOCUSABLE))?.focus();
    } else {
      this.#releaseScrollLock();
      if (this.#hasOpenedDrawer) burger?.focus();
    }
  };

  /** Scroll lock without layout shift. */
  #applyScrollLock = () => {
    const gap = window.innerWidth - document.documentElement.clientWidth;
    this.#scrollLock = {
      overflow: document.body.style.overflow,
      paddingRight: document.body.style.paddingRight,
    };
    document.body.style.overflow = 'hidden';
    if (gap > 0) document.body.style.paddingRight = `${gap}px`;
  };

  #releaseScrollLock = () => {
    if (!this.#scrollLock) return;
    document.body.style.overflow = this.#scrollLock.overflow;
    document.body.style.paddingRight = this.#scrollLock.paddingRight;
    this.#scrollLock = null;
  };

  /* ----------------------------------------------------------------------
     Keyboard
     -------------------------------------------------------------------- */

  #onKeydown = (event) => {
    if (this.#drawerOpen) {
      this.#trapDrawerFocus(event);
      return;
    }

    if (event.key !== 'Escape' || !this.#openPanel) return;
    const trigger = this.querySelector(`[data-trigger="${CSS.escape(this.#openPanel)}"]`);
    this.#setPanel(null);
    if (trigger instanceof HTMLElement) trigger.focus();
  };

  #trapDrawerFocus = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeDrawer();
      return;
    }
    if (event.key !== 'Tab') return;

    const drawer = this.querySelector('[data-drawer]');
    if (!drawer) return;

    const items = Array.from(drawer.querySelectorAll(FOCUSABLE)).filter(
      (el) => el instanceof HTMLElement && el.offsetParent !== null
    );
    if (!items.length) return;

    const first = items[0];
    const last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };
}

if (!customElements.get('awh-header-component')) {
  customElements.define('awh-header-component', AwhHeader);
}
